import { useEffect, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Link,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { CreateNewFolder, Folder as FolderIcon } from "@mui/icons-material";
import AppDialog from "components/common/AppDialog";
import {
  useLazyGetConnectFolderOptionsQuery,
  useCreateConnectFolderMutation,
} from "features/fileSystem/filesStorage";

type BucketMode = "storage" | "sftp";

export interface FolderPickerResult {
  bucket: string;
  folder: string;
  subfolders: string[];
  isSftpBucket: boolean;
}

interface SftpFolderPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: FolderPickerResult) => void;
  userId?: number | null;
  initialBucket?: string;
  initialFolder?: string;
  initialSubfolders?: string[];
}

export default function SftpFolderPickerDialog({
  open,
  onClose,
  onConfirm,
  userId,
  initialBucket,
  initialFolder,
  initialSubfolders,
}: SftpFolderPickerDialogProps) {
  const [bucketMode, setBucketMode] = useState<BucketMode>("storage");
  const [browsePrefix, setBrowsePrefix] = useState("");
  const [rootPrefix, setRootPrefix] = useState("");
  const [selectedFolder, setSelectedFolder] = useState("");
  const [selectedSubfolders, setSelectedSubfolders] = useState<Set<string>>(new Set());
  const [resolvedBucket, setResolvedBucket] = useState("");

  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderError, setNewFolderError] = useState("");

  const [triggerOptions, optionsResult] = useLazyGetConnectFolderOptionsQuery();
  const [createFolder, { isLoading: isCreatingFolder }] = useCreateConnectFolderMutation();

  useEffect(() => {
    if (!open) return;
    const startMode: BucketMode = initialBucket?.includes("sftp") ? "sftp" : "storage";
    setBucketMode(startMode);
    setSelectedSubfolders(new Set(initialSubfolders ?? []));

    if (startMode === "sftp") {
      setRootPrefix("");
      setBrowsePrefix(initialFolder || "");
      setSelectedFolder(initialFolder || "");
    } else if (userId) {
      const base = `users/${userId}`;
      setRootPrefix(base);
      setBrowsePrefix(initialFolder || base);
      setSelectedFolder(initialFolder || "");
    } else {
      setRootPrefix("");
      setBrowsePrefix(initialFolder || "");
      setSelectedFolder(initialFolder || "");
    }
  }, [open, userId, initialBucket, initialFolder, initialSubfolders]);

  useEffect(() => {
    if (!open) return;
    const isSftp = bucketMode === "sftp";
    const params: { is_sftp_bucket: boolean; user_id?: number; prefix?: string } = {
      is_sftp_bucket: isSftp,
    };
    if (!isSftp && userId) params.user_id = userId;
    if (browsePrefix) params.prefix = browsePrefix;
    void triggerOptions(params);
  }, [open, bucketMode, browsePrefix, userId, triggerOptions]);

  useEffect(() => {
    if (optionsResult.data?.bucket) {
      setResolvedBucket(optionsResult.data.bucket);
    }
    if (bucketMode !== "sftp" && optionsResult.data?.base_prefix && !rootPrefix && userId) {
      const base = optionsResult.data.base_prefix.replace(/\/+$/, "");
      setRootPrefix(base);
    }
  }, [optionsResult.data, userId, rootPrefix, bucketMode]);

  const items = optionsResult.data?.items ?? [];
  const browseNorm = browsePrefix.replace(/\/+$/, "");
  const rootNorm = rootPrefix.replace(/\/+$/, "");

  const breadcrumbSegments = browsePrefix
    .split("/")
    .filter(Boolean);

  const handleFolderClick = (prefix: string) => {
    const norm = prefix.replace(/\/+$/, "");
    setBrowsePrefix(norm);
    setSelectedFolder(norm);
    setSelectedSubfolders(new Set());
  };

  const handleSubfolderToggle = (name: string) => {
    setSelectedSubfolders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleCreateFolder = async () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) return;
    if (/[/\\]/.test(trimmed)) {
      setNewFolderError("Folder name must not contain slashes");
      return;
    }
    const existing = items.find((it) => it.name === trimmed);
    if (existing) {
      setNewFolderError("A folder with this name already exists");
      return;
    }
    try {
      await createFolder({
        is_sftp_bucket: bucketMode === "sftp",
        prefix: browsePrefix,
        folder_name: trimmed,
      }).unwrap();
      setNewFolderMode(false);
      setNewFolderName("");
      setNewFolderError("");
      void triggerOptions({
        is_sftp_bucket: bucketMode === "sftp",
        ...(!( bucketMode === "sftp") && userId ? { user_id: userId } : {}),
        ...(browsePrefix ? { prefix: browsePrefix } : {}),
      });
    } catch {
      setNewFolderError("Failed to create folder");
    }
  };

  const isInSubfolderMode = selectedFolder && browseNorm === selectedFolder;

  const handleConfirm = () => {
    const folder = selectedFolder || browseNorm;
    onConfirm({
      bucket: resolvedBucket,
      folder,
      subfolders: Array.from(selectedSubfolders),
      isSftpBucket: bucketMode === "sftp",
    });
    onClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Select Folder"
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedFolder}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Confirm
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <ToggleButtonGroup
          value={bucketMode}
          exclusive
          onChange={(_e, val) => {
            if (!val) return;
            setBucketMode(val);
            if (val === "sftp") {
              setBrowsePrefix("");
              setRootPrefix("");
            } else {
              const base = userId ? `users/${userId}` : "";
              setBrowsePrefix(base);
              setRootPrefix(base);
            }
            setSelectedFolder("");
            setSelectedSubfolders(new Set());
          }}
          size="small"
          fullWidth
        >
          <ToggleButton value="storage" sx={{ textTransform: "none", fontWeight: 600 }}>
            Storage (after extraction)
          </ToggleButton>
          <ToggleButton value="sftp" sx={{ textTransform: "none", fontWeight: 600 }}>
            SFTP (for integration)
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Breadcrumbs
            separator="/"
            sx={{
              flex: 1,
              minWidth: 0,
              "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap", overflow: "hidden" },
              "& .MuiBreadcrumbs-li": { minWidth: 0 },
            }}
          >
            <Link
              underline="hover"
              color={browseNorm === rootNorm ? "text.primary" : "inherit"}
              onClick={(e) => {
                e.preventDefault();
                setBrowsePrefix(rootNorm || "");
                setSelectedFolder("");
                setSelectedSubfolders(new Set());
              }}
              sx={{ cursor: "pointer", fontSize: "0.85rem", whiteSpace: "nowrap" }}
            >
              Root
            </Link>
            {breadcrumbSegments.map((seg, idx, arr) => {
              const prefix = arr.slice(0, idx + 1).join("/");
              const isLast = idx === arr.length - 1;
              return (
                <Link
                  key={prefix}
                  underline="hover"
                  color={isLast ? "text.primary" : "inherit"}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isLast) return;
                    setBrowsePrefix(prefix);
                    setSelectedFolder("");
                    setSelectedSubfolders(new Set());
                  }}
                  sx={{
                    cursor: isLast ? "default" : "pointer",
                    fontSize: "0.85rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 160,
                  }}
                >
                  {seg}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>

        {/* New folder inline form */}
        {newFolderMode ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              size="small"
              value={newFolderName}
              onChange={(e) => {
                setNewFolderName(e.target.value);
                setNewFolderError("");
              }}
              placeholder="Folder name"
              error={!!newFolderError}
              helperText={newFolderError || undefined}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateFolder();
                if (e.key === "Escape") {
                  setNewFolderMode(false);
                  setNewFolderName("");
                  setNewFolderError("");
                }
              }}
              sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || isCreatingFolder}
              sx={{ textTransform: "none", fontWeight: 600, minWidth: 70, mt: 0.25 }}
            >
              {isCreatingFolder ? "..." : "Create"}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setNewFolderMode(false);
                setNewFolderName("");
                setNewFolderError("");
              }}
              sx={{ textTransform: "none", mt: 0.25 }}
            >
              Cancel
            </Button>
          </Box>
        ) : (
          <Button
            size="small"
            startIcon={<CreateNewFolder />}
            onClick={() => setNewFolderMode(true)}
            sx={{ textTransform: "none", fontWeight: 600, alignSelf: "flex-start" }}
          >
            New Folder
          </Button>
        )}

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {optionsResult.isFetching ? (
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading folders...
              </Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No subfolders found. {selectedFolder ? "This folder is selected." : "Navigate into a folder to select it."}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
              {items.map((it) => {
                const name = it.name;
                const prefix = String(it.prefix || "").replace(/\/+$/, "");
                const isSelected = prefix === selectedFolder;
                return (
                  <Box
                    key={prefix}
                    sx={{
                      px: 2,
                      py: 0.75,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "action.hover" },
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: isSelected ? "rgba(31,93,107,0.06)" : undefined,
                    }}
                  >
                    {isInSubfolderMode ? (
                      <Checkbox
                        size="small"
                        checked={selectedSubfolders.has(name)}
                        onChange={() => handleSubfolderToggle(name)}
                        sx={{ p: 0.25 }}
                      />
                    ) : null}
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}
                      onClick={() => handleFolderClick(prefix)}
                    >
                      <FolderIcon fontSize="small" sx={{ color: "rgb(31,93,107)" }} />
                      <Typography variant="body2" noWrap>
                        {name}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {selectedFolder && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: "rgba(31,93,107,0.04)",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
              Selected
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
              {resolvedBucket}/{selectedFolder}
            </Typography>
            {selectedSubfolders.size > 0 ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Subfolders: {Array.from(selectedSubfolders).join(", ")}
              </Typography>
            ) : isInSubfolderMode && items.length > 0 ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                No subfolders selected — access will be granted to all subfolders
              </Typography>
            ) : null}
          </Box>
        )}
      </Box>
    </AppDialog>
  );
}
